import {
  asFiles,
  asStringArray,
  FIELD_TYPES,
  isEmptyValue,
  selectOption,
  type CollectionData,
  type FieldDef,
  type FileMeta,
  type RecordData,
  type ViewDef,
} from '@tomo/core';
import { cx, Tag, Tooltip } from '@tomo/ui';
import { Flag } from 'lucide-react';
import type { CSSProperties, HTMLAttributes, Ref } from 'react';
import { useProject } from '../../data/hooks.tsx';
import { fileKind, formatBytes, formatDuration } from '../../lib/files.ts';
import { Icon } from '../../lib/icons.tsx';
import { AssetThumb } from '../assets/AssetThumb.tsx';
import { FieldValue, VoteButton } from '../fields/display.tsx';
import { visibleFields } from './view-utils.ts';

/**
 * The image on top of a card. Unless the view points at a field, it's the record's own cover —
 * every record has one, so a gallery works without anyone setting a cover field up.
 */
export function useCoverFile(collection: CollectionData, view: ViewDef, record: RecordData): FileMeta | null {
  const project = useProject();
  const coverField = view.coverFieldId ? collection.fields.find((f) => f.id === view.coverFieldId) : undefined;
  if (!coverField) return record.cover;
  if (coverField.type === 'file') return asFiles(record.values[coverField.id]).find((f) => f.mime.startsWith('image/')) ?? null;
  if (coverField.type === 'relation') {
    for (const id of asStringArray(record.values[coverField.id])) {
      const linked = project.getRecord(id);
      if (linked?.cover) return linked.cover;
    }
  }
  return record.cover;
}

export interface RecordCardProps extends HTMLAttributes<HTMLDivElement> {
  record: RecordData;
  collection: CollectionData;
  view: ViewDef;
  variant: 'board' | 'gallery';
  /** Field used for board columns (not repeated on the card). */
  groupFieldId?: string | null;
  dragging?: boolean;
  ref?: Ref<HTMLDivElement>;
  style?: CSSProperties;
}

export function RecordCard({ record, collection, view, variant, groupFieldId, dragging, className, ...rest }: RecordCardProps) {
  const cover = useCoverFile(collection, view, record);
  const skip = new Set([groupFieldId, view.coverFieldId].filter(Boolean) as string[]);
  const fields = visibleFields(collection, view).filter((f) => !skip.has(f.id) && f.type !== 'file');
  const byRole = (role: FieldDef['role']) => fields.find((f) => f.role === role);

  return (
    <div className={cx('rcard', className)} data-variant={variant} data-size={view.cardSize} data-dragging={dragging || undefined} {...rest}>
      {cover ? (
        <div className="rcard__cover">
          <AssetThumb file={cover} />
        </div>
      ) : variant === 'gallery' ? (
        <div className="rcard__cover rcard__cover--empty" data-color={collection.color}>
          <Icon token={record.icon ?? collection.icon} size={34} />
        </div>
      ) : null}
      <div className="rcard__body">
        <CardTitle record={record} />
        <CardFields record={record} collection={collection} fields={fields.filter((f) => f.type !== 'votes')} max={variant === 'gallery' ? 6 : 4} />
        {byRole('votes') && (
          <div className="rcard__foot">
            <VoteButton record={record} field={byRole('votes')} size="sm" />
          </div>
        )}
      </div>
    </div>
  );
}

function CardTitle({ record }: { record: RecordData }) {
  return (
    <div className="rcard__title">
      {record.icon && <Icon token={record.icon} size={15} className="rcard__icon" />}
      <span className={cx(!record.title && 'value-empty')}>{record.title || 'Untitled'}</span>
    </div>
  );
}

/** Tags first, then a compact stat block — reads like a game card. */
function CardFields({ record, collection, fields, max }: { record: RecordData; collection: CollectionData; fields: FieldDef[]; max: number }) {
  const filled = fields.filter((f) => (f.type === 'formula' ? true : !isEmptyValue(f, record.values[f.id])));
  const tagLike = filled.filter((f) => f.type === 'select' || f.type === 'multiSelect');
  const stats = filled.filter((f) => FIELD_TYPES[f.type].numeric && f.type !== 'checkbox' && f.type !== 'votes');
  const rest = filled.filter((f) => !tagLike.includes(f) && !stats.includes(f) && f.type !== 'checkbox');
  return (
    <>
      {tagLike.length > 0 && (
        <div className="rcard__tags">
          {tagLike.slice(0, 3).map((f) => (
            <FieldValue key={f.id} record={record} field={f} collection={collection} variant="card" />
          ))}
        </div>
      )}
      {stats.length > 0 && (
        <div className="rcard__stats">
          {stats.slice(0, max).map((f) => (
            <div key={f.id} className="rcard__stat">
              <span className="rcard__stat-name g-truncate" title={f.name}>
                {f.name}
              </span>
              <span className="rcard__stat-value">
                <FieldValue record={record} field={{ ...f, config: { ...f.config, display: 'number' } }} collection={collection} variant="card" />
              </span>
            </div>
          ))}
        </div>
      )}
      {rest.slice(0, 2).map((f) => (
        <div key={f.id} className="rcard__row" data-labeled="">
          <span className="rcard__row-name g-truncate" title={f.name}>
            {f.name}
          </span>
          <FieldValue record={record} field={f} collection={collection} variant="card" />
        </div>
      ))}
    </>
  );
}

